/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import { useGroupAuth } from "@app/vue/providers/GroupAuth";

type TabItem = {
  to: string;
  icon: string;
  label: string;
};

export const GroupMobileTabBar = defineComponent({
  name: "GroupMobileTabBar",
  setup() {
    const route = useRoute();
    const { t } = useLocalization();
    const { isGroupAdmin } = useGroupAuth();

    const tabs = computed<TabItem[]>(() => [
      {
        to: "/group/dashboard",
        icon: "dashboard",
        label: t("admin.common.dashboard"),
      },
      {
        to: "/group/loyalty",
        icon: "loyalty",
        label: t("nav.labels.loyalty"),
      },
      ...(isGroupAdmin.value
        ? [
            {
              to: "/group/settings",
              icon: "settings",
              label: t("admin.common.settings"),
            },
          ]
        : []),
    ]);

    const isActive = (to: string) => route.path.startsWith(to);

    const tabClasses = (active: boolean) => [
      "relative flex min-w-0 flex-1 select-none flex-col items-center justify-center gap-0.5 px-0.5 pb-2 pt-0 text-[10px] leading-none transition-colors",
      active ? "text-primary" : "text-base-content",
    ];

    return () => (
      <nav
        aria-label={t("nav.labels.mobile_navigation")}
        class="fixed inset-x-0 border-t bottom-0 h-[calc(3.75rem+env(safe-area-inset-bottom))] border-t bg-base-200 pb-[env(safe-area-inset-bottom)] shadow-lg backdrop-blur-md min-[699px]:hidden"
      >
        <div class="mx-auto flex h-[3.75rem] max-w-lg items-stretch">
          {tabs.value.map((tab) => {
            const active = isActive(tab.to);
            return (
              <RouterLink
                to={tab.to}
                class={tabClasses(active)}
                aria-current={active ? "page" : undefined}
              >
                <span class="relative leading-none">
                  <span class="material-symbols-outlined text-[23px] leading-none">
                    {tab.icon}
                  </span>
                </span>
                <span class="w-full truncate px-0.5 text-center uppercase">
                  {tab.label}
                </span>
              </RouterLink>
            );
          })}

          <RouterLink
            to="/group/menu"
            class={tabClasses(isActive("/group/menu"))}
            aria-current={isActive("/group/menu") ? "page" : undefined}
          >
            <span class="material-symbols-outlined text-[23px] leading-none">
              menu
            </span>
            <span class="w-full truncate px-0.5 text-center uppercase">
              {t("nav.labels.menu")}
            </span>
          </RouterLink>
        </div>
      </nav>
    );
  },
});
